import {forEach, isEqual} from 'lodash';
import {useEffect, useCallback, useRef, useState} from 'react';
import Audio, {LocalAudioTrackPublication, Room, RemoteParticipant, RemoteTrack} from 'twilio-video';

import {usePrevious} from 'hooks';
import {MediaDevicesConstraints} from 'types';

type Props = {
    room: Room;
    mediaConstraints?: MediaDevicesConstraints;
    isAudioEnabled?: boolean;
    onLocalNetworkQualityLevelChange?: (level: number) => void;
    onRemoteNetworkQualityLevelChange?: (level: number) => void;
}

export const TwilioAudio = ({
    room, mediaConstraints, isAudioEnabled = true, onLocalNetworkQualityLevelChange, onRemoteNetworkQualityLevelChange
}: Props) => {
    const remoteMediaRef = useRef(null);

    const [localParticipantConnected, setLocalParticipantConnected] = useState(false);
    const [roomSubscribed, setRoomSubscribed] = useState(false);

    const prevAudioInputDevice = usePrevious(mediaConstraints?.audioinput);
    const prevAudioOutputDevice = usePrevious(mediaConstraints?.audiooutput);

    const toggleLocalAudio = useCallback(() => {
        const localAudioTrackPublications: LocalAudioTrackPublication[] =
            Array.from(room.localParticipant.audioTracks.values());

        localAudioTrackPublications
            .map((publicationTrack) => publicationTrack.track)
            .forEach((track) => {
                if (isAudioEnabled) {
                    track.enable();
                } else {
                    track.disable();
                }
            });
    }, [room.localParticipant, isAudioEnabled]);

    const attachTrack = useCallback((track: RemoteTrack) => {
        if (track.kind === 'audio') {
            remoteMediaRef.current?.appendChild(track.attach());
        }
    }, [remoteMediaRef]);

    const detachTrack = useCallback((track: RemoteTrack) => {
        if (track.kind === 'audio') {
            track.mediaStreamTrack.enabled = false;
            track.detach().forEach((element) => {
                element.remove();
                element.srcObject = null;
            });
        }
    }, []);

    const updateAudioInputDevice = useCallback((props: Audio.CreateLocalTrackOptions) => {
        Array.from(room.localParticipant.audioTracks.values()).forEach((audio) => {
            audio.track.restart(props);
        });
    }, [room.localParticipant]);

    const updateAudioOutputDevice = useCallback((deviceId: string) => {
        const audioElements = remoteMediaRef.current.getElementsByTagName('audio');
        forEach(Array.from(audioElements), (audioEl: any) => audioEl?.setSinkId?.(deviceId));
    }, []);

    const handleLocalParticipantConnected = useCallback(() => {
        const participant = room.localParticipant;
        participant.audioTracks.forEach((pub) => {
            participant.publishTrack(pub.track);
            updateAudioOutputDevice(mediaConstraints?.audiooutput);
        });
        if (onLocalNetworkQualityLevelChange) {
            onLocalNetworkQualityLevelChange(participant.networkQualityLevel);
            participant.on('networkQualityLevelChanged', onLocalNetworkQualityLevelChange);
        }
    }, [room.localParticipant, onLocalNetworkQualityLevelChange, updateAudioOutputDevice, mediaConstraints?.audiooutput]);

    const handleRemoteParticipantConnected = useCallback((participant: RemoteParticipant) => {
        participant.on('trackSubscribed', attachTrack);
        participant.on('trackUnsubscribed', detachTrack);
        if (onRemoteNetworkQualityLevelChange) {
            onRemoteNetworkQualityLevelChange(participant.networkQualityLevel);
            participant.on('networkQualityLevelChanged', onRemoteNetworkQualityLevelChange);
        }
    }, [attachTrack, detachTrack, onRemoteNetworkQualityLevelChange]);

    const cleanupLocalParticipantTracks = useCallback(() => {
        const publicationTracks = Array.from(room.localParticipant.audioTracks.values());
        publicationTracks.forEach((publication) => {
            if (publication.track) {
                publication.track.stop();
                room.localParticipant.unpublishTrack(publication.track);
            }
        });
    }, [room]);

    const cleanupRemoteParticipantTracks = useCallback((participant: RemoteParticipant) => {
        const publicationTracks = Array.from(participant.audioTracks.values());
        publicationTracks.forEach((publication) => {
            if (publication.track) {
                detachTrack(publication.track);
            }
        });
    }, [detachTrack]);

    const subscribeRoomEvents = useCallback(() => {
        room.participants.forEach(handleRemoteParticipantConnected);
        room.on('participantConnected', handleRemoteParticipantConnected);
        room.on('participantDisconnected', cleanupRemoteParticipantTracks);
        room.on('disconnected', () => {
            cleanupLocalParticipantTracks();
            room.participants.forEach(cleanupRemoteParticipantTracks);
        });
    }, [room, handleRemoteParticipantConnected, cleanupRemoteParticipantTracks, cleanupLocalParticipantTracks]);

    useEffect(() => {
        if (!!mediaConstraints?.audioinput && prevAudioInputDevice &&
            !isEqual(prevAudioInputDevice, mediaConstraints?.audioinput)
        ) {
            updateAudioInputDevice(mediaConstraints.audioinput as MediaTrackConstraintSet);
        }
    }, [mediaConstraints, prevAudioInputDevice, prevAudioOutputDevice, updateAudioInputDevice, updateAudioOutputDevice]);

    useEffect(() => {
        if (!!mediaConstraints?.audioinput && prevAudioInputDevice &&
            !isEqual(prevAudioInputDevice, mediaConstraints?.audioinput)
        ) {
            updateAudioInputDevice(mediaConstraints.audioinput as MediaTrackConstraintSet);
        }
    }, [mediaConstraints?.audioinput, prevAudioInputDevice, updateAudioInputDevice]);

    useEffect(() => {
        if (!localParticipantConnected) {
            handleLocalParticipantConnected();
            setLocalParticipantConnected(true);
        }
    }, [localParticipantConnected, handleLocalParticipantConnected]);

    useEffect(() => {
        if (!roomSubscribed && remoteMediaRef.current) {
            setRoomSubscribed(true);
            subscribeRoomEvents();
        }
    }, [remoteMediaRef, roomSubscribed, subscribeRoomEvents]);

    useEffect(() => () => {
        cleanupLocalParticipantTracks();
    }, [cleanupLocalParticipantTracks]);

    useEffect(() => {
        toggleLocalAudio();
    }, [toggleLocalAudio]);

    return (<div ref={remoteMediaRef} className='tw-hidden' />);
};